import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ActionsButtonsComponent.css';

interface ActionsButtonsProps {
    isModified: boolean;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
}

/**
 * Sticky-top "unsaved changes" banner.
 *
 * Replaces the old floating bottom bar. The banner is `position: sticky;
 * top: 0` so it stays visible while the user scrolls through a long form.
 * Renders nothing when nothing was modified.
 *
 * Also registers a `beforeunload` handler while modified so the browser
 * prompts the user before they close the tab / navigate away. SPA route
 * changes are NOT intercepted here — Settings tabs are inside the same
 * SPA route, so unsaved data persists until the next save / discard.
 */
const ActionsButtonsComponent: React.FC<ActionsButtonsProps> = ({
    isModified,
    onSave,
    onCancel,
}) => {
    const { t } = useTranslation();
    // QA §5.2: guard against a double-click firing two saves. Awaits the
    // (possibly async) onSave and keeps both buttons disabled meanwhile.
    const [saving, setSaving] = useState(false);
    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
    };
    useEffect(() => {
        if (!isModified) return;
        const handler = (e: BeforeUnloadEvent) => {
            // Standard pattern — returnValue triggers the browser dialog.
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isModified]);

    if (!isModified) return null;

    return (
        <div className="gs-unsaved-bar" role="alert">
            <span className="gs-unsaved-bar__icon" aria-hidden="true">●</span>
            <span className="gs-unsaved-bar__message">
                {t('common.unsavedChanges')}
            </span>
            <div className="gs-unsaved-bar__actions">
                <button
                    type="button"
                    className="gs-unsaved-bar__discard"
                    data-cy="actions-cancel-btn"
                    onClick={onCancel}
                    disabled={saving}
                >
                    {t('common.discard')}
                </button>
                <button
                    type="button"
                    className="gs-unsaved-bar__save"
                    data-cy="actions-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? t('common.working') : t('common.save')}
                </button>
            </div>
        </div>
    );
};

export default ActionsButtonsComponent;
